/**
 * Утиліти для роботи з маршрутами та URL
 * @utils RouteUtils
 * @file RouteUtils.ts
 * @path frontend/src/utils/RouteUtils.ts
 */

import { PUBLIC_ROUTES, PRIVATE_ROUTES } from "../constants/routes";

/**
 * Типи для маршрутів
 */
export type PublicRoute = (typeof PUBLIC_ROUTES)[keyof typeof PUBLIC_ROUTES];
export type PrivateRoute = (typeof PRIVATE_ROUTES)[keyof typeof PRIVATE_ROUTES];

/**
 * Типи для параметрів навігації
 */
export interface NavigationParams {
  [key: string]: string | number | boolean;
}

/**
 * Утиліти для роботи з маршрутами
 */
export const RouteUtils = {
  /**
   * Генерує URL з параметрами
   * @param route - базовий маршрут з плейсхолдерами (:id, :statusCode, etc.)
   * @param params - об'єкт з параметрами для заміни
   * @returns сформований URL
   */
  generatePath(route: string, params: NavigationParams = {}): string {
    return Object.keys(params).reduce((path, key) => {
      const value = String(params[key]);
      return path.replace(`:${key}`, encodeURIComponent(value));
    }, route);
  },

  /**
   * Перевіряє, чи є маршрут приватним
   * @param route - маршрут для перевірки
   * @returns true, якщо маршрут приватний
   */
  isPrivateRoute(route: string): boolean {
    return Object.values(PRIVATE_ROUTES).includes(route as PrivateRoute);
  },

  /**
   * Перевіряє, чи є маршрут публічним
   * @param route - маршрут для перевірки
   * @returns true, якщо маршрут публічний
   */
  isPublicRoute(route: string): boolean {
    return Object.values(PUBLIC_ROUTES).includes(route as PublicRoute);
  },

  /**
   * Отримує ім'я маршруту за шляхом
   * @param path - шлях маршруту
   * @returns ім'я маршруту або null, якщо не знайдено
   */
  getRouteName(path: string): string | null {
    const publicEntry = Object.entries(PUBLIC_ROUTES).find(
      ([, value]) => value === path
    );
    if (publicEntry) return publicEntry[0];

    const privateEntry = Object.entries(PRIVATE_ROUTES).find(
      ([, value]) => value === path
    );
    return privateEntry ? privateEntry[0] : null;
  },

  /**
   * Перевіряє, чи відповідає шлях шаблону маршруту з параметрами
   * @param path - шлях для перевірки
   * @param pattern - шаблон маршруту з параметрами
   * @returns true, якщо шлях відповідає шаблону
   */
  matchesPattern(path: string, pattern: string): boolean {
    const patternRegex = new RegExp(
      "^" + pattern.replace(/:[^/]+/g, "([^/]+)") + "$"
    );
    return patternRegex.test(path);
  },

  /**
   * Отримує параметри з динамічного маршруту
   * @param path - шлях маршруту
   * @param pattern - шаблон маршруту з параметрами
   * @returns об'єкт з параметрами
   */
  extractParams(path: string, pattern: string): Record<string, string> {
    const params: Record<string, string> = {};
    const pathSegments = path.split("/");
    const patternSegments = pattern.split("/");

    patternSegments.forEach((segment, index) => {
      if (segment.startsWith(":") && pathSegments[index]) {
        const paramName = segment.slice(1);
        params[paramName] = decodeURIComponent(pathSegments[index]);
      }
    });

    return params;
  },

  /**
   * Перевіряє, чи поточний маршрут активний
   * @param currentPath - поточний шлях
   * @param targetRoute - цільовий маршрут для перевірки
   * @returns true, якщо маршрут активний
   */
  isRouteActive(currentPath: string, targetRoute: string): boolean {
    if (currentPath === targetRoute) return true;

    // Для динамічних маршрутів
    if (targetRoute.includes(":")) {
      return this.matchesPattern(currentPath, targetRoute);
    }

    return false;
  },

  /**
   * Додає query параметри до URL
   * @param path - базовий шлях
   * @param queryParams - об'єкт з query параметрами
   * @returns URL з query параметрами
   */
  addQueryParams(
    path: string,
    queryParams: Record<string, string | number | boolean>
  ): string {
    const url = new URL(path, window.location.origin);

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });

    return `${url.pathname}${url.search}${url.hash}`;
  },

  /**
   * Отримує query параметри з URL
   * @param search - search строка (location.search)
   * @returns об'єкт з query параметрами
   */
  getQueryParams(search: string): Record<string, string> {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(search);

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    return params;
  },
};
